<template>
  <v-app-bar
    app
    color="white"
    elevation="0"
    height="80"
    class="dealer-header"
    flat
  >
    <v-container class="d-flex align-center px-6">
      <!-- Logo -->
      <div class="dealer-logo">
        <router-link to="/dealers/login" class="logo-link">
          <img
            src="@/assets/img/logo.png"
            alt="AutoSync™"
            height="85"
            class="logo-image"
          />
          <div class="logo-text">
            <span class="brand-name">AutoSyncX™</span>
            <span class="dealer-badge">Dealer Portal</span>
          </div>
        </router-link>
      </div>

      <v-spacer></v-spacer>

      <!-- Desktop Navigation -->
      <div class="dealer-nav d-none d-lg-flex" v-if="!$vuetify.breakpoint.mobile">
        <v-btn
          text
          class="nav-btn"
          @click="scrollToSection('features')"
        >
          <i class="material-icons-round nav-icon">star</i>
          Features
        </v-btn>
        <v-btn
          text
          class="nav-btn"
          @click="scrollToSection('how-it-works')"
        >
          <i class="material-icons-round nav-icon">settings</i>
          How It Works
        </v-btn>
        <v-btn
          text
          class="nav-btn"
          @click="scrollToSection('testimonials')"
        >
          <i class="material-icons-round nav-icon">reviews</i>
          Testimonials
        </v-btn>
        <v-btn
          text
          class="nav-btn"
          href="tel:+1234567890"
        >
          <i class="material-icons-round nav-icon">phone</i>
          Contact
        </v-btn>
      </div>

      <!-- CTA Buttons -->
      <div class="dealer-cta d-none d-md-flex">
        <v-btn
          outlined
          class="login-btn mr-4"
          @click="loginDealer"
        >
          <i class="material-icons-round btn-icon">login</i>
          Login
        </v-btn>
        <v-btn
          class="signup-btn"
          @click="signUpDealer"
        >
          <i class="material-icons-round btn-icon">rocket_launch</i>
          Get Started
        </v-btn>
      </div>

      <!-- Mobile Menu Button -->
      <v-btn
        icon
        @click="toggleMobileMenu"
        class="d-lg-none mobile-menu-btn"
        :class="{ 'menu-open': mobileMenu }"
      >
        <i class="material-icons-round">{{ mobileMenu ? 'close' : 'menu' }}</i>
      </v-btn>
    </v-container>

    <!-- Mobile Navigation Menu -->
    <div v-if="mobileMenu" class="mobile-menu-overlay" @click="closeMobileMenu">
      <div class="mobile-menu-content" @click.stop>
        <div class="mobile-menu-header">
          <div class="mobile-logo">
            <img
              src="@/assets/img/logo.png"
              alt="AutoSync™"
              height="40"
            />
            <span class="dealer-badge-mobile">Dealer Portal</span>
          </div>
          <v-btn icon @click="closeMobileMenu" class="close-btn">
            <i class="material-icons-round">close</i>
          </v-btn>
        </div>

        <div class="mobile-menu-items">
          <div class="mobile-menu-item" @click="scrollToSection('features')">
            <i class="material-icons-round">star</i>
            <span>Features</span>
          </div>

          <div class="mobile-menu-item" @click="scrollToSection('how-it-works')">
            <i class="material-icons-round">settings</i>
            <span>How It Works</span>
          </div>

          <div class="mobile-menu-item" @click="scrollToSection('testimonials')">
            <i class="material-icons-round">group</i>
            <span>Testimonials</span>
          </div>

          <div class="mobile-menu-item" @click="navigateToAbout">
            <i class="material-icons-round">info</i>
            <span>About</span>
          </div>

          <div class="mobile-menu-item" @click="closeMobileMenu">
            <i class="material-icons-round">phone</i>
            <span>Contact</span>
          </div>

          <div class="mobile-menu-divider"></div>

          <div class="mobile-menu-item mobile-login-item" @click="loginDealer">
            <i class="material-icons-round">login</i>
            <span>Login</span>
          </div>

          <div class="mobile-menu-item mobile-signup-item" @click="signUpDealer">
            <i class="material-icons-round">rocket_launch</i>
            <span>Get Started</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Original Navigation Drawer (Hidden) -->
    <v-navigation-drawer
      v-show="false"
      temporary
      right
      fixed
      app
      class="mobile-nav-drawer d-none"
      overlay-opacity="0.5"
      width="300"
      :z-index="2000"
    >

    </v-navigation-drawer>
  </v-app-bar>
</template>

<script>
export default {
  name: "HeaderDealer",
  data() {
    return {
      mobileMenu: false
    }
  },
  watch: {
    '$route'() {
      // Close mobile menu when route changes
      this.mobileMenu = false;
    },
    mobileMenu(newVal) {
      // Prevent body scroll when mobile menu is open
      if (newVal) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  },
  methods: {
    toggleMobileMenu() {
      this.mobileMenu = !this.mobileMenu;
    },
    closeMobileMenu() {
      this.mobileMenu = false;
    },
    scrollToSection(sectionId) {
      this.closeMobileMenu();
      this.$nextTick(() => {
        const element = document.querySelector(`.${sectionId}-section`);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    },
    loginDealer() {
      this.closeMobileMenu();
      // Navigate to dealer login
      this.$router.push('/dealers/login');
    },
    signUpDealer() {
      this.closeMobileMenu();
      // Navigate to dealer signup
      this.$router.push('/dealers/signup');
    },
    navigateToAbout() {
      this.closeMobileMenu();
      // Navigate to about page and scroll to top
      this.$router.push('/dealers/about');
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  },
  beforeDestroy() {
    // Clean up body overflow style
    document.body.style.overflow = '';
  }
}
</script>

<style scoped>
.dealer-header {
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.dealer-logo {
  display: flex;
  align-items: center;
}

.logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  position: relative;
  transition: all 0.3s ease;
}

.logo-link:hover {
  transform: translateY(-1px);
}

.logo-image {
  margin-right: 15px;
  transition: all 0.3s ease;
}

.logo-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.brand-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  line-height: 1;
  margin-bottom: 2px;
  font-family: 'Inter', sans-serif;
  letter-spacing: -0.02em;
}

.dealer-badge {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  transition: all 0.3s ease;
}

.dealer-badge-mobile {
  background: linear-gradient(135deg, #1a202c 0%, #2c3e50 100%);
  color: white;
  padding: 3px 8px;
  border-radius: 15px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-left: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.dealer-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 40px;
}

.nav-btn {
  color: #4a5568 !important;
  font-weight: 500 !important;
  text-transform: none !important;
  font-size: 0.9rem !important;
  padding: 8px 16px !important;
  border-radius: 8px !important;
  transition: all 0.3s ease !important;
  font-family: 'Inter', sans-serif !important;
}

.nav-btn:hover {
  background-color: rgba(59, 130, 246, 0.08) !important;
  color: #3b82f6 !important;
  transform: translateY(-1px) !important;
}

.nav-icon {
  font-size: 18px !important;
  margin-right: 6px !important;
  opacity: 0.8;
}

.dealer-cta {
  display: flex;
  align-items: center;
  margin-left: 30px;
  gap: 12px;
}

.login-btn {
  border: 2px solid #e2e8f0 !important;
  color: #4a5568 !important;
  font-weight: 500 !important;
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 8px 20px !important;
  transition: all 0.3s ease !important;
  font-family: 'Inter', sans-serif !important;
}

.login-btn:hover {
  border-color: #3b82f6 !important;
  color: #3b82f6 !important;
  background-color: rgba(59, 130, 246, 0.05) !important;
  transform: translateY(-1px) !important;
}

.signup-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 10px 24px !important;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
  transition: all 0.3s ease !important;
  font-family: 'Inter', sans-serif !important;
}

.signup-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4) !important;
}

.btn-icon {
  font-size: 16px !important;
  margin-right: 6px !important;
}

.mobile-menu-btn {
  color: #4a5568 !important;
  transition: all 0.3s ease !important;
  z-index: 1001 !important;
}

.mobile-menu-btn:hover {
  background-color: rgba(59, 130, 246, 0.08) !important;
  color: #3b82f6 !important;
}

.mobile-menu-btn.menu-open {
  color: #3b82f6 !important;
  background-color: rgba(59, 130, 246, 0.1) !important;
}

/* Custom Mobile Menu Styles */
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  justify-content: flex-end;
  animation: fadeIn 0.3s ease;
}

.mobile-menu-content {
  width: 300px;
  height: 100vh;
  background: #ffffff;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  animation: slideInRight 0.3s ease;
}

.mobile-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e2e8f0;
}

.mobile-menu-items {
  flex: 1;
  padding: 20px 0;
  overflow-y: auto;
}

.mobile-menu-item {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  margin: 4px 12px;
}

.mobile-menu-item:hover {
  background-color: rgba(59, 130, 246, 0.08);
  transform: translateX(4px);
}

.mobile-menu-item i {
  font-size: 20px;
  margin-right: 16px;
  color: #4a5568;
}

.mobile-menu-item span {
  font-weight: 500;
  color: #2d3748;
  font-family: 'Inter', sans-serif;
}

.mobile-menu-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 16px 20px;
}

.mobile-login-item {
  border: 1px solid #e2e8f0;
  margin: 8px 12px;
}

.mobile-login-item:hover {
  border-color: #3b82f6;
  background-color: rgba(59, 130, 246, 0.05);
}

.mobile-signup-item {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  margin: 8px 12px;
}

.mobile-signup-item i,
.mobile-signup-item span {
  color: white;
}

.mobile-signup-item:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  transform: translateX(2px);
}

.close-btn {
  color: #4a5568;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideInRight {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}



.mobile-logo {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .dealer-header {
    height: 64px !important;
    padding: 0 16px !important;
  }

  .dealer-header .v-container {
    padding: 0 8px !important;
  }

  .logo-image {
    height: 35px !important;
    margin-right: 10px !important;
  }

  .brand-name {
    font-size: 1.2rem !important;
  }

  .dealer-badge {
    font-size: 0.6rem !important;
    padding: 2px 6px !important;
  }

  .mobile-menu-btn {
    margin-left: 8px !important;
  }

  .mobile-nav-drawer {
    width: 280px !important;
  }
}

@media (max-width: 480px) {
  .dealer-header {
    height: 56px !important;
  }

  .logo-image {
    height: 30px !important;
    margin-right: 8px !important;
  }

  .brand-name {
    font-size: 1.1rem !important;
  }

  .dealer-badge {
    font-size: 0.55rem !important;
    padding: 2px 5px !important;
  }

  .mobile-nav-drawer {
    width: 260px !important;
  }
}
</style>
